import VerofreshRecord from './VerofreshRecord'
import { isNil, isPlainObject, map } from 'lodash'

/**
 * One logged API request. Read only: the api_access_log table is append only
 * and its v2 endpoint exposes index and view alone.
 *
 * Root users only. The endpoint answers 404 to anyone else, and the API strips
 * query_params, ip_address and user_agent from the payload for a non-root
 * caller, so those three are only ever populated here for a root user.
 */
class ApiAccessLogRecord extends VerofreshRecord {
  static get endpoint () { return 'api-access-log' }

  /**
   * When the request was served, as an ISO 8601 string.
   *
   * created_at arrives already formatted: api\modules\v2\Module::init()
   * installs common\components\JsonResponseFormatter, whose stringifyDatetime()
   * converts the VFCarbon the model carries into ISO 8601 Zulu, so the wire
   * value is "2026-07-27T20:30:07Z". This accessor exists only to give the
   * template one place to read and to keep a null safe.
   *
   * @return {String|null}
   */
  get createdAtIso () {
    return isNil(this.created_at) ? null : String(this.created_at)
  }

  /**
   * The request's query parameters as rows for a two column table.
   *
   * The API stores them as a JSON object and replaces the value of any
   * credential bearing parameter with a redaction marker before saving, so
   * what arrives here is already safe to display. Nested values are shown as
   * JSON rather than flattened, since a query string nests at most a level or
   * two and the raw shape is what an operator is looking for.
   *
   * Returns an empty list when the stored value is not an object. The API
   * stores whatever it could not parse into parameters unchanged rather than
   * dropping it, so that case is real and is rendered by queryParametersText
   * instead.
   *
   * @return {Array} Objects with parameter and value keys.
   */
  get queryParameterRows () {
    if (!isPlainObject(this.query_params)) {
      return []
    }

    return map(this.query_params, (value, parameter) => ({
      parameter: parameter,
      value: isPlainObject(value) || Array.isArray(value) ? JSON.stringify(value) : value
    }))
  }

  /**
   * The stored query parameters when they are not a set of key value pairs,
   * for example an unparseable string the API preserved as it arrived. Null
   * when there is nothing to show this way.
   *
   * @return {String|null}
   */
  get queryParametersText () {
    if (isNil(this.query_params) || isPlainObject(this.query_params)) {
      return null
    }

    return String(this.query_params)
  }
}

export default ApiAccessLogRecord
